import React, { useState, useEffect, useCallback } from 'react';
import { logger } from '../utils/logger';
import { 
  getDimensions, 
  deleteDimension, 
  purgeDimension,
  getDimensionUsageStats,
  exportDimension
} from '../services/featureService';
import DimensionEditor from './DimensionEditor';
import { notifyDimensionOrTagListChanged } from '../utils/dimensionTagSync';
import './Dimensions.css';

const DIMENSION_TYPE_LABELS = {
  'BOOLEAN': 'Boolean',
  'TRI_STATE': 'Yes-No',
  'RATING': 'Rating'
};

const Dimensions = ({ featureGroupId, userRole }) => {
  const [dimensions, setDimensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingDimension, setEditingDimension] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [usageStats, setUsageStats] = useState(new Map());
  const [loadingStats, setLoadingStats] = useState(new Set());

  const isOwner = userRole === 'Owner';

  // Define loadUsageStats before loadDimensions that uses it
  const loadUsageStats = useCallback(async (dims) => {
    const statsMap = new Map();
    const loadingSet = new Set();
    
    for (const dim of dims) {
      loadingSet.add(dim.id);
      try {
        const stats = await getDimensionUsageStats(dim.id);
        statsMap.set(dim.id, stats);
      } catch (err) {
        logger.error(`Error loading stats for dimension ${dim.id}:`, err);
        statsMap.set(dim.id, { percentage: 0, features_with_tags: 0, total_features: 0 });
      } finally {
        loadingSet.delete(dim.id);
      }
    }
    
    setUsageStats(statsMap);
    setLoadingStats(loadingSet);
  }, []);

  const loadDimensions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getDimensions(featureGroupId);
      setDimensions(data);
      
      // Load usage stats for all dimensions
      if (data && data.length > 0) {
        loadUsageStats(data);
      }
    } catch (err) {
      setError('Failed to load dimensions');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  }, [featureGroupId, loadUsageStats]);

  useEffect(() => {
    if (featureGroupId && isOwner) {
      loadDimensions();
    }
  }, [featureGroupId, isOwner, loadDimensions]);

  const handleAdd = () => {
    setEditingDimension(null);
    setShowEditor(true);
  };

  const handleEdit = (dimension) => {
    setEditingDimension(dimension);
    setShowEditor(true);
  };

  const handleDelete = async (dimension) => {
    // Get usage stats to show in warning
    let stats = usageStats.get(dimension.id);
    if (!stats) {
      try {
        stats = await getDimensionUsageStats(dimension.id);
      } catch (err) {
        logger.error('Error loading stats for delete warning:', err);
        stats = { features_with_tags: 0 };
      }
    }

    const message = `Are you sure you want to delete the dimension "${dimension.name}"?\n\n` +
      `This dimension has ${stats.features_with_tags || 0} feature(s) with tags.\n\n` +
      `This will hide the dimension from the UI, but you can purge it later to permanently delete it.`;
    
    if (!window.confirm(message)) {
      return;
    }

    try {
      await deleteDimension(dimension.id);
      notifyDimensionOrTagListChanged(featureGroupId);
      await loadDimensions();
    } catch (err) {
      alert('Failed to delete dimension');
      logger.error(err);
    }
  };

  const handlePurge = async (dimension) => {
    // Get usage stats to show in warning
    let stats = usageStats.get(dimension.id);
    if (!stats) {
      try {
        stats = await getDimensionUsageStats(dimension.id);
      } catch (err) {
        logger.error('Error loading stats for purge warning:', err);
        stats = { features_with_tags: 0 };
      }
    }

    const message = `Are you sure you want to PERMANENTLY delete the dimension "${dimension.name}"?\n\n` +
      `This will delete ${stats.features_with_tags || 0} feature tag assignment(s) and cannot be undone.`;
    
    if (!window.confirm(message)) {
      return;
    }

    try {
      await purgeDimension(dimension.id);
      notifyDimensionOrTagListChanged(featureGroupId);
      await loadDimensions();
    } catch (err) {
      alert('Failed to purge dimension');
      logger.error(err);
    }
  };

  const handleExport = async (dimension) => {
    try {
      const exportData = await exportDimension(dimension.id);
      const jsonStr = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${dimension.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_dimension.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to export dimension');
      logger.error(err);
    }
  };

  const handleEditorClose = () => {
    setShowEditor(false);
    setEditingDimension(null);
    loadDimensions();
  };

  const getPermissionsLabel = (dimension) => {
    const vis = [];
    const edit = [];
    
    if (dimension.visible_to_guest) vis.push('Guest');
    if (dimension.visible_to_editor) vis.push('Editor');
    if (dimension.visible_to_owner) vis.push('Owner');
    
    if (dimension.editable_to_guest) edit.push('Guest');
    if (dimension.editable_to_editor) edit.push('Editor');
    if (dimension.editable_to_owner) edit.push('Owner');
    
    return `View: ${vis.join(', ') || 'None'} | Edit: ${edit.join(', ') || 'None'}`;
  };

  if (!isOwner) {
    return (
      <div className="dimensions-content">
        <div className="dimensions-header">
          <h3>Tag Dimensions</h3>
          <p className="info-message">Only Feature Map Owners can manage dimensions.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dimensions-content">
      <div className="dimensions-header">
        <h3>Tag Dimensions</h3>
        <div className="dimensions-header-actions">
          <button 
            className="import-dimension-button"
            onClick={() => {
              // TODO: Implement import dialog
              alert('Import functionality coming soon');
            }}
            title="Import dimension from JSON"
          >
            <i className="fas fa-file-import"></i> Import
          </button>
          <button className="add-dimension-button" onClick={handleAdd}>
            <i className="fas fa-plus"></i> Add Dimension
          </button>
        </div>
      </div>

      {loading && <div className="loading">Loading dimensions...</div>}
      {error && <div className="error">{error}</div>}

      {!loading && !error && (
        <div className="dimensions-list">
          {dimensions.length === 0 ? (
            <div className="no-dimensions">
              No dimensions yet. Click "Add Dimension" to create one.
            </div>
          ) : (
            <table className="dimensions-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Permissions</th>
                  <th>Usage</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {dimensions.map(dimension => {
                  const stats = usageStats.get(dimension.id);
                  const isLoading = loadingStats.has(dimension.id);
                  
                  return (
                    <tr key={dimension.id} className={dimension.deleted_at ? 'deleted' : ''}>
                      <td className="dimension-name">
                        {dimension.name}
                        {dimension.deleted_at && (
                          <span className="deleted-badge">Deleted</span>
                        )}
                      </td>
                      <td>
                        <span className={`dimension-type-badge type-${dimension.dimension_type.toLowerCase()}`}>
                          {DIMENSION_TYPE_LABELS[dimension.dimension_type] || dimension.dimension_type}
                        </span>
                      </td>
                      <td className="dimension-permissions">
                        {getPermissionsLabel(dimension)}
                      </td>
                      <td className="dimension-usage">
                        {isLoading ? (
                          <span className="loading-stats">Loading...</span>
                        ) : stats ? (
                          <span className="usage-stats">
                            {stats.percentage}% ({stats.features_with_tags} features)
                          </span>
                        ) : (
                          <span className="no-stats">—</span>
                        )}
                      </td>
                      <td className="dimension-actions">
                        <button
                          className="action-button"
                          onClick={() => handleEdit(dimension)}
                          title="Edit dimension"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          className="action-button"
                          onClick={() => handleExport(dimension)}
                          title="Export dimension"
                        >
                          <i className="fas fa-download"></i>
                        </button>
                        {dimension.deleted_at ? (
                          <button
                            className="action-button purge"
                            onClick={() => handlePurge(dimension)}
                            title="Permanently delete dimension"
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        ) : (
                          <button
                            className="action-button delete"
                            onClick={() => handleDelete(dimension)}
                            title="Delete dimension"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {showEditor && (
        <DimensionEditor
          dimension={editingDimension}
          featureGroupId={featureGroupId}
          onClose={handleEditorClose}
        />
      )}
    </div>
  );
};

export default Dimensions;


